import React from 'react';
import SourceAttribution from './investigation/SourceAttribution';
import ResultKeywordTags from './investigation/ResultKeywordTags';
import { FEED_CHIP } from './investigation/feedCardStyles';
import { collectDetailEntries, formatDetailScalar } from '../utils/feedInspectDetails';
import { collectSupplementalInspectLinks, resolveInspectUrl } from '../utils/feedInspectUtils';
import { stripHtmlTags } from '../utils/personFeedDisplay';
import InspectDrawerShell, { OpenLinkButton } from './InspectDrawerShell';

function DetailBlock({ label, children }) {
  if (!children) return null;
  return (
    <section className="rounded-xl border border-white/10 bg-slate-900/60 p-3">
      <h4 className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">{label}</h4>
      <div className="mt-2 text-sm leading-relaxed text-slate-200">{children}</div>
    </section>
  );
}

function formatRecordDate(data) {
  const raw =
    data?.source_date ||
    data?.published_at ||
    data?.date ||
    data?.details?.date ||
    data?.details?.filed_at ||
    '';
  const s = String(raw || '').trim();
  return s ? s.slice(0, 40) : null;
}

/**
 * Generic feed inspect — right slide-over drawer.
 *
 * @param {{ isOpen: boolean, onClose: Function, data?: object }} props
 */
export default function GenericFeedInspectModal({ isOpen, onClose, data }) {
  if (!isOpen || !data) return null;

  const details = data.details && typeof data.details === 'object' ? data.details : {};
  const title = String(data.title || data.display_name || 'Intelligence record').trim();
  const snippet = stripHtmlTags(String(data.snippet || data.summary || '').trim());
  const primaryUrl = resolveInspectUrl(data);
  const links = collectSupplementalInspectLinks(data);
  const subdomains = Array.isArray(details.subdomains) ? details.subdomains : [];
  const asnRecords = Array.isArray(details.asn_records) ? details.asn_records : [];
  const alerts = Array.isArray(data.alerts) ? data.alerts : [];
  const detailEntries = collectDetailEntries(details);
  const scalarEntries = detailEntries.filter((row) => row.kind === 'scalar');
  const arrayEntries = detailEntries.filter((row) => row.kind === 'array');
  const objectEntries = detailEntries.filter((row) => row.kind === 'object');
  const recordDate = formatRecordDate(data);
  const extractedEntities = [
    ...(Array.isArray(data.matched_tags) ? data.matched_tags : []),
    ...(Array.isArray(details.entities) ? details.entities : []),
    data.target_entity,
    data.entity_checked,
  ]
    .map((e) => String(e || '').trim())
    .filter(Boolean)
    .filter((v, i, arr) => arr.findIndex((x) => x.toLowerCase() === v.toLowerCase()) === i)
    .slice(0, 12);

  return (
    <InspectDrawerShell
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel="Record inspection"
      testId="generic-feed-inspect-modal"
      eyebrow={<SourceAttribution res={data} fallback="Intelligence record" />}
      title={title}
      sourceHref={primaryUrl}
      subtitle={
        recordDate ? (
          <p className="mt-1 text-xs text-slate-400" data-testid="inspect-record-date">
            {recordDate}
          </p>
        ) : null
      }
    >
      {snippet ? (
        <DetailBlock label="Summary">
          <p className="text-slate-300">{snippet}</p>
        </DetailBlock>
      ) : null}

      {extractedEntities.length > 0 ? (
        <DetailBlock label="Extracted entities">
          <div className="flex flex-wrap gap-1">
            {extractedEntities.map((ent) => (
              <span key={ent} className={FEED_CHIP}>
                {ent}
              </span>
            ))}
          </div>
        </DetailBlock>
      ) : null}

      <ResultKeywordTags res={data} max={3} />

      {primaryUrl ? (
        <p className="break-all font-mono text-[10px] text-slate-500" data-testid="inspect-source-url">
          {primaryUrl}
        </p>
      ) : null}

      {links
        .filter((link) => link.url !== primaryUrl)
        .map((link) => (
          <div key={`${link.label}-${link.url}`} className="flex flex-wrap items-center gap-2">
            <OpenLinkButton href={link.url} label={`${link.label} ↗`} />
          </div>
        ))}

      {subdomains.length > 0 ? (
        <DetailBlock label="Subdomains">
          <div className="max-h-40 overflow-y-auto rounded-lg border border-white/10 bg-slate-950/70 p-2">
            <ul className="m-0 list-none space-y-0.5 p-0">
              {subdomains.slice(0, 80).map((subd) => (
                <li key={subd} className="text-xs font-mono text-cyan-300">
                  {subd}
                </li>
              ))}
            </ul>
          </div>
        </DetailBlock>
      ) : null}

      {asnRecords.length > 0 ? (
        <DetailBlock label="ASN routing">
          <ul className="space-y-2">
            {asnRecords.map((row) => (
              <li key={`${row.ip}-${row.asn}`} className="rounded-lg border border-white/5 bg-slate-950/80 p-2">
                <p className="font-mono text-xs text-cyan-200">
                  {row.asn || 'Unknown ASN'} — {row.asn_name || row.ip}
                </p>
                {row.country_code ? (
                  <p className="mt-1 text-[11px] text-slate-400">Country: {row.country_code}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </DetailBlock>
      ) : null}

      {alerts.length > 0 ? (
        <DetailBlock label="Alerts">
          <ul className="space-y-2">
            {alerts.map((alert, idx) => (
              <li key={idx} className="rounded-lg border border-white/5 bg-slate-950/80 p-2 text-sm">
                <p className="font-semibold text-slate-100">{alert.matched_name || alert.title || 'Alert'}</p>
                {alert.reason ? <p className="mt-1 text-xs text-slate-400">{alert.reason}</p> : null}
                {alert.url && alert.url !== '#' ? (
                  <div className="mt-2">
                    <OpenLinkButton href={alert.url} label="Open Link ↗" />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </DetailBlock>
      ) : null}

      {scalarEntries.length > 0 ? (
        <DetailBlock label="Details">
          <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {scalarEntries.slice(0, 16).map((row) => (
              <div key={row.key} className="rounded-lg border border-white/5 bg-slate-950/70 p-2">
                <dt className="text-[9px] font-black uppercase tracking-wider text-slate-500">{row.label}</dt>
                <dd className="mt-1 break-all font-mono text-[11px] text-slate-200">
                  {formatDetailScalar(row.value)}
                </dd>
              </div>
            ))}
          </dl>
        </DetailBlock>
      ) : null}

      {arrayEntries.map((row) => (
        <DetailBlock key={row.key} label={row.label}>
          <div className="flex flex-wrap gap-1">
            {row.value.slice(0, 32).map((item) => (
              <span key={String(item)} className={FEED_CHIP}>
                {String(item)}
              </span>
            ))}
          </div>
        </DetailBlock>
      ))}

      {objectEntries.map((row) => (
        <DetailBlock key={row.key} label={row.label}>
          <pre className="overflow-x-auto rounded-lg border border-white/5 bg-slate-950/80 p-2 font-mono text-[10px] text-slate-300">
            {JSON.stringify(row.value, null, 2)}
          </pre>
        </DetailBlock>
      ))}
    </InspectDrawerShell>
  );
}
